import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import { parseStringToNumber } from "../../../../../helperFunctions";
import {
  useAddLoanProductMutation,
  useEditLoanProductMutation,
} from "../../../../../redux/features/loans/loans.api";
import {
  AddLoanProductPayload,
  LoanProduct,
  LoanRepaymentMethod,
} from "../../../../../redux/features/loans/loans.type";
import { loanrepaymentMethodMap } from "../../../../../utils/constants";
import { extraLoanProductConfigurationSchema } from "../../../../../validationSchema";
import { Button } from "../../../../buttons/button";
import { Icon } from "../../../../icons/icon";
import { Checkbox } from "../../../../inputs/checkbox";
import { TextInput } from "../../../../inputs/textInput";
import { ErrorText } from "../../../../typography/errorText";

export const MoreConfiguration = ({
  finalPayload,
  mode,
  selectedProduct,
  onSuccess,
}: {
  finalPayload: AddLoanProductPayload;
  mode: "create" | "edit";
  selectedProduct?: LoanProduct;
  onSuccess: () => void;
}) => {
  const [addProduct, { isLoading: addingProduct }] =
    useAddLoanProductMutation();
  const [editProduct, { isLoading: editingProduct }] =
    useEditLoanProductMutation();

  const {
    values,
    errors,
    handleBlur,
    handleSubmit,
    touched,
    isValid,
    setFieldValue,
    handleChange,
  } = useFormik({
    initialValues: {
      minLoanAmount: finalPayload?.minLoanAmount,

      maxXAmount: finalPayload?.maxXAmount,
      maxInterestRate: finalPayload?.maxInterestRate,

      penaltyFee: finalPayload?.penaltyFee,
      loanApplicationFee: finalPayload?.loanApplicationFee,

      multipleRunningLoan: finalPayload.multipleRunningLoan,
      EnableDisbursement: finalPayload.EnableDisbursement,

      paymentMode: finalPayload.paymentMode,
    },
    enableReinitialize: true,
    validationSchema: extraLoanProductConfigurationSchema,
    onSubmit: async (data) => {
      const payload = { ...finalPayload, ...data };

      const parsedPayload = {
        ...payload,
        maxGuarantor: parseStringToNumber(payload.maxGuarantor)!,
        minMonthJoined: parseStringToNumber(payload.minMonthJoined)!,
        maxTenure: parseStringToNumber(payload.maxTenure)!,
        minLoanAmount: parseStringToNumber(payload.minLoanAmount)!,
        maxXAmount: parseStringToNumber(payload.maxXAmount)!,
        maxInterestRate: parseStringToNumber(payload.maxInterestRate)!,
        penaltyFee: parseStringToNumber(payload.penaltyFee)!,
        loanApplicationFee: parseStringToNumber(payload.loanApplicationFee)!,
      };

      if (mode === "create") {
        const resp = await addProduct(parsedPayload);

        if (resp.data?.success) {
          onSuccess();
        }
      } else {
        const resp = await editProduct({
          id: `${selectedProduct?.id}`,
          payload: parsedPayload,
        });

        if (resp.data?.success) {
          onSuccess();
        }
      }
    },
  });

  return (
    <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit}>
      <NumericFormat
        suffix={undefined}
        prefix={undefined}
        decimalScale={2}
        thousandSeparator
        allowLeadingZeros={false}
        customInput={TextInput}
        onBlur={handleBlur}
        onChange={handleChange}
        type="text"
        name={"minLoanAmount"}
        value={values.minLoanAmount}
        placeholder="Enter amount"
        errors={
          errors?.minLoanAmount && touched?.minLoanAmount
            ? errors?.minLoanAmount
            : ""
        }
        inputClass=""
        className=""
        customIcon={
          <div>
            <Icon svg={"naira"} width={20} height={20} />
          </div>
        }
        customIconPosition="left"
        hasTitle
        title="Minimum Loan Amount"
      />

      <NumericFormat
        suffix={undefined}
        prefix={undefined}
        decimalScale={0}
        thousandSeparator
        allowLeadingZeros={false}
        customInput={TextInput}
        onBlur={handleBlur}
        onChange={handleChange}
        type="text"
        name={"maxXAmount"}
        value={values.maxXAmount}
        placeholder="Enter multiplier"
        errors={
          errors?.maxXAmount && touched?.maxXAmount ? errors?.maxXAmount : ""
        }
        inputClass=""
        className="!p-0 !pl-4"
        customIcon={
          <div
            className="
                  font-medium text-sm md:text-sm
                  py-[14px] px-[16px]
                  bg-white
                  border border-pry-dark-green-300/10
                  rounded-e-lg
                  !min-w-fit text-center
                  "
          >
            x of contributions
          </div>
        }
        customIconPosition="right"
        hasTitle
        title="Maximum Loan Amount Multiplier"
      />

      <NumericFormat
        suffix={undefined}
        prefix={undefined}
        decimalScale={2}
        thousandSeparator
        allowLeadingZeros={false}
        customInput={TextInput}
        onBlur={handleBlur}
        onChange={handleChange}
        type="text"
        name={"maxInterestRate"}
        value={values.maxInterestRate}
        placeholder="Enter rate"
        errors={
          errors?.maxInterestRate && touched?.maxInterestRate
            ? errors?.maxInterestRate
            : ""
        }
        inputClass=""
        className="!p-0 !pl-4"
        customIcon={
          <div
            className="
                  font-medium text-sm md:text-sm
                  py-[14px] px-[16px]
                  bg-white min-w-fit
                  border border-pry-dark-green-300/10
                  rounded-e-lg
                  "
          >
            % p.a
          </div>
        }
        customIconPosition="right"
        hasTitle
        title="Interest Rate (%)"
      />

      <NumericFormat
        suffix={undefined}
        prefix={undefined}
        decimalScale={2}
        thousandSeparator
        allowLeadingZeros={false}
        customInput={TextInput}
        onBlur={handleBlur}
        onChange={handleChange}
        type="text"
        name={"penaltyFee"}
        value={values.penaltyFee}
        placeholder="Enter fee"
        errors={
          errors?.penaltyFee && touched?.penaltyFee ? errors?.penaltyFee : ""
        }
        inputClass=""
        className="!p-0 !pl-4"
        customIcon={
          <div
            className="
                  font-medium text-sm md:text-sm
                  py-[14px] px-[16px]
                  bg-white
                  border border-pry-dark-green-300/10
                  rounded-e-lg
                  "
          >
            %
          </div>
        }
        customIconPosition="right"
        hasTitle
        title="Penalty Fee (%)"
      />

      <NumericFormat
        suffix={undefined}
        prefix={undefined}
        decimalScale={2}
        thousandSeparator
        allowLeadingZeros={false}
        customInput={TextInput}
        onBlur={handleBlur}
        onChange={handleChange}
        type="text"
        name={"loanApplicationFee"}
        value={values.loanApplicationFee}
        placeholder="Enter amount"
        errors={
          errors?.loanApplicationFee && touched?.loanApplicationFee
            ? errors?.loanApplicationFee
            : ""
        }
        inputClass=""
        className=""
        customIcon={
          <div>
            <Icon svg={"naira"} width={20} height={20} />
          </div>
        }
        customIconPosition="left"
        hasTitle
        title="Loan Processing Fee"
      />

      <div className="flex flex-col gap-3">
        <p className="flex flex-col gap-0.5 text-sm text-pry-dark-green-300">
          <span className="font-bold">Repayment Methods</span>
          <span>Select at least one way the loan can be repaid</span>
        </p>

        <div className="flex flex-col gap-2">
          {Object.keys(loanrepaymentMethodMap)
            .filter((k) => (k as LoanRepaymentMethod) !== "wallet")
            .map((k) => {
              const mode = k as LoanRepaymentMethod;

              const { configText, configSubtext } =
                loanrepaymentMethodMap[mode];

              return (
                <Checkbox
                  key={mode}
                  className="!items-start"
                  checked={values.paymentMode.includes(mode)}
                  onCheck={() => {
                    setFieldValue("paymentMode", [...values.paymentMode, mode]);
                  }}
                  onUnCheck={() => {
                    setFieldValue("paymentMode", [
                      ...values.paymentMode.filter((v) => v !== mode),
                    ]);
                  }}
                  text={
                    <p className="text-sm flex flex-col">
                      <span className="font-medium text-pry-dark-green-300/80">
                        {configText}
                      </span>
                      <span className="text-pry-dark-green-300/50">
                        {configSubtext}
                      </span>
                    </p>
                  }
                />
              );
            })}
        </div>

        <ErrorText text={(errors.paymentMode as string) ?? ""} />
      </div>

      <div className="flex flex-col gap-3">
        <p className="flex flex-col gap-0.5 text-sm text-pry-dark-green-300">
          <span className="font-bold">Other Configuration</span>
          <span>Define loan eligibility and disbursement rules.</span>
        </p>

        <div className="flex flex-col gap-2">
          <Checkbox
            checked={values.multipleRunningLoan}
            className="!items-start"
            onCheck={() => {
              setFieldValue("multipleRunningLoan", true);
            }}
            onUnCheck={() => {
              setFieldValue("multipleRunningLoan", false);
            }}
            text={
              <p className="text-sm flex flex-col">
                <span className="font-medium text-pry-dark-green-300/80">
                  Allow members have multiple running loans?
                </span>
                <span className="text-pry-dark-green-300/50">
                  Allow members to have more than one active loan for this loan
                  product at the same time.
                </span>
              </p>
            }
          />

          <Checkbox
            checked={values.EnableDisbursement}
            className="!items-start"
            onCheck={() => {
              setFieldValue("EnableDisbursement", true);
            }}
            onUnCheck={() => {
              setFieldValue("EnableDisbursement", false);
            }}
            text={
              <p className="text-sm flex flex-col">
                <span className="font-medium text-pry-dark-green-300/80">
                  Enable auto-disbursment on approval?
                </span>
                <span className="text-pry-dark-green-300/50">
                  Allow system to automatically disburse the loan immediately
                  after it passes all approval levels.
                </span>
              </p>
            }
          />
        </div>
      </div>

      <hr className="border-pry-dark-green-300/10" />

      <Button
        type="submit"
        className="w-full"
        variation="primary"
        text="Save"
        disabled={!isValid}
        loading={editingProduct || addingProduct}
      />
    </form>
  );
};
